import { clsx } from "clsx";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { UnitImage } from "@/components/units/unit/info/UnitImage";
import { formatUnitDimensions } from "@/utils/units/format";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility";
import { useFacility } from "@/hooks/useFacility";
import { QuoteType } from "@/types/enums";
import { CallForAvailability } from "@/components/units/actions";
import type { UnitGroup } from "@/types";

interface SkeletonUnitItemProps {
    unitGroup: UnitGroup;
    layout: "grid" | "row";
}

/**
 * SkeletonUnitItem — renders a unit card with real cached data (image, dimensions,
 * category, features) and skeleton loaders for dynamic data (price, buttons, promotions).
 * Urgency messaging is not displayed.
 */
export const SkeletonUnitItem = ({ unitGroup, layout = "row" }: SkeletonUnitItemProps) => {
    const { representative_unit } = unitGroup;
    const displayOptions = useDisplayOptionContext();
    const { disableSoftReservation, language } = displayOptions;
    const { facility, isResolved: isFacilityResolved } = useFacility(
        representative_unit.facility_id,
    );
    const {
        hasAvailableUnits,
        canRent,
        canReserve,
        shouldShowSelectButton,
    } = useTriggerButtonVisibility(unitGroup);

    const dimensionLabel = formatUnitDimensions(
        representative_unit.width,
        representative_unit.length,
    );

    const rentLabel = language?.action?.rentButton ?? "Rent";
    const reserveLabel = language?.action?.reserveButton ?? "Reserve";
    const selectLabel = language?.action?.selectButton ?? "Select";
    const quoteLabel = language?.action?.quoteButton ?? "Get a Quote";
    const phone = facility?.phone || "";
    const quoteType = facility?.settings?.quote_type;

    // quote_type-dependent slot (soft reservation already known to be enabled).
    // Until the facility query resolves, quote_type is unknown — show a neutral
    // placeholder so "Call for Availability" doesn't flash before Get a Quote.
    const renderQuoteOrCall = () =>
        !isFacilityResolved ? (
            <Skeleton className="tw:h-9 tw:w-full tw:rounded-full" />
        ) : quoteType === QuoteType.RESERVE ? (
            <Button variant="secondary" className="soft-reservation-btn" type="button" disabled>
                {quoteLabel}
            </Button>
        ) : (
            <CallForAvailability phone={phone} />
        );

    const renderDisabledButtons = () => {
        if (hasAvailableUnits) {
            if (shouldShowSelectButton()) {
                return (
                    <Button variant="default" type="button" aria-label={selectLabel} disabled>
                        {selectLabel}
                    </Button>
                );
            }

            return (
                <div>
                    {canRent && (
                        <Button variant="default" className="rent-btn" aria-label={rentLabel} type="button" disabled>
                            {rentLabel}
                        </Button>
                    )}
                    {canReserve && (
                        <Button variant="secondary" className="reserve-btn" aria-label={reserveLabel} type="button" disabled>
                            {reserveLabel}
                        </Button>
                    )}
                    {!canRent && !canReserve && (
                        <>
                            {disableSoftReservation ? (
                                <CallForAvailability phone={phone} />
                            ) : (
                                renderQuoteOrCall()
                            )}
                        </>
                    )}
                </div>
            );
        }

        // No available units
        if (disableSoftReservation) {
            return <CallForAvailability phone={phone} />;
        }

        return renderQuoteOrCall();
    };

    return (
        <div className={clsx("unit-item", "unit-item--" + layout)}>
            <div className="unit-item__image">
                <UnitImage
                    category={representative_unit.category}
                    length={representative_unit.length}
                    width={representative_unit.width}
                />
            </div>
            <div className="unit-item__dimension">
                <span>{dimensionLabel}</span>
            </div>
            <div className="unit-item__category">
                {representative_unit.category?.name ?? ""}
            </div>
            <div className="unit-item__features">
                <ul className="unit-item-features">
                    {representative_unit.features.length > 0
                        ? representative_unit.features.map((feature) => (
                              <li
                                  key={feature.id}
                                  className="unit-item-features__item"
                                  id={`feature-${feature.id}`}
                              >
                                  <span>
                                      <span className="feature-icon"></span>
                                      {feature.name}
                                  </span>
                              </li>
                          ))
                        : null}
                </ul>
            </div>
            <div className="unit-item__promotions"></div>
            <div className="unit-item__urgency"></div>
            <div className="unit-item__price">
                <Skeleton className="tw:h-4 tw:mb-2 tw:w-[75px]" />
                <Skeleton className="tw:h-4 tw:w-[100px]" />
            </div>
            <div className="unit-item__action">
                {renderDisabledButtons()}
            </div>
        </div>
    );
};
